import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  inject,
  effect,
  DestroyRef,
  untracked,
  NgZone
} from '@angular/core';
import { TenantStore } from '../../stores/tenant.store';
import { LoggerService } from '@core/services/logger.service';

/**
 * HIGH-004: Configuration for directive behavior
 */
const HAS_FEATURE_CONFIG = {
  /** Debounce time in ms for effect updates to prevent excessive re-renders */
  EFFECT_DEBOUNCE_MS: 16, // ~1 frame at 60fps
} as const;

/**
 * HasFeatureDirective
 *
 * Structural directive that conditionally renders content based on tenant feature access.
 *
 * DEV-201: Tenant Feature Access Control
 *
 * Usage:
 * ```html
 * <!-- Show content only if tenant has 'trading' feature -->
 * <div *appHasFeature="'trading'">
 *   Trading features here
 * </div>
 *
 * <!-- With else template -->
 * <div *appHasFeature="'advanced-analytics'; else noAccess">
 *   Advanced analytics content
 * </div>
 * <ng-template #noAccess>
 *   <p>Upgrade to access analytics</p>
 * </ng-template>
 *
 * <!-- Negate with 'not' -->
 * <div *appHasFeature="'trading'; not: true">
 *   Show when trading is NOT available
 * </div>
 * ```
 */
@Directive({
  selector: '[appHasFeature]',
  standalone: true
})
export class HasFeatureDirective {
  private readonly tenantStore = inject(TenantStore);
  private readonly templateRef = inject(TemplateRef<HasFeatureContext>);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  private readonly logger = inject(LoggerService);

  private isRendered = false;
  private feature = '';
  private negate = false;
  private elseTemplateRef: TemplateRef<HasFeatureContext> | null = null;

  /**
   * HIGH-004: Debounce timer for effect updates
   */
  private updateDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * The feature to check for
   */
  @Input()
  set appHasFeature(feature: string) {
    this.feature = feature;
    this.updateView();
  }

  /**
   * Template to show when feature is not available
   */
  @Input()
  set appHasFeatureElse(templateRef: TemplateRef<HasFeatureContext> | null) {
    this.elseTemplateRef = templateRef;
    this.updateView();
  }

  /**
   * Negate the condition (show when feature is NOT available)
   */
  @Input()
  set appHasFeatureNot(negate: boolean) {
    this.negate = negate;
    this.updateView();
  }

  constructor() {
    // React to tenant feature changes with debouncing
    // HIGH-004: Debounced effect to prevent excessive re-renders
    effect(() => {
      // Access the features signal to track changes
      const features = this.tenantStore.tenantFeatures();

      // Debounce the update to prevent rapid re-renders during batch feature updates
      untracked(() => {
        this.scheduleUpdate();
      });
    });

    // Cleanup debounce timer on destroy
    this.destroyRef.onDestroy(() => {
      if (this.updateDebounceTimer !== null) {
        clearTimeout(this.updateDebounceTimer);
        this.updateDebounceTimer = null;
      }
    });
  }

  /**
   * HIGH-004: Schedule debounced view update
   * Prevents excessive re-renders when features change rapidly
   */
  private scheduleUpdate(): void {
    if (this.updateDebounceTimer !== null) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = setTimeout(() => {
      this.updateDebounceTimer = null;
      // Run inside NgZone to ensure change detection
      this.ngZone.run(() => {
        this.updateView();
      });
    }, HAS_FEATURE_CONFIG.EFFECT_DEBOUNCE_MS);
  }

  private updateView(): void {
    // LOW-001: Log error for empty feature name
    if (!this.feature) {
      this.logger.warn('[HasFeatureDirective] Empty feature name provided - directive will not render content. ' +
        'Ensure a valid feature name is passed to *appHasFeature.');
      return;
    }

    const hasFeature = this.tenantStore.hasAccessToFeature(this.feature);
    const shouldRender = this.negate ? !hasFeature : hasFeature;

    if (shouldRender && !this.isRendered) {
      // Render main template
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef, {
        $implicit: this.feature,
        appHasFeature: this.feature,
        hasFeature: true
      });
      this.isRendered = true;
    } else if (!shouldRender && this.isRendered) {
      // Clear and optionally render else template
      this.viewContainer.clear();
      if (this.elseTemplateRef) {
        this.viewContainer.createEmbeddedView(this.elseTemplateRef, {
          $implicit: this.feature,
          appHasFeature: this.feature,
          hasFeature: false
        });
      }
      this.isRendered = false;
    } else if (!shouldRender && !this.isRendered && this.elseTemplateRef) {
      // Initial render of else template
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.elseTemplateRef, {
        $implicit: this.feature,
        appHasFeature: this.feature,
        hasFeature: false
      });
    }
  }
}

/**
 * Context for the HasFeatureDirective template
 */
export interface HasFeatureContext {
  $implicit: string;
  appHasFeature: string;
  hasFeature: boolean;
}
